import { useNavigate } from 'react-router-dom';
import { useLanguageLearning } from '../contexts/LanguageLearningContext';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getLearningLanguageTranslationKey } from '../constants/learningLanguages';
import { useAuth } from '../contexts/AuthContext';

/**
 * HeroLanguagePicker - Prominent language selector for the Hero section
 * Shows all available learning languages as pill buttons
 */
const HeroLanguagePicker = ({
  className = '',
  labelClassName = '',
  listClassName = '',
  pillClassName = '',
  showLabel = true,
}) => {
  const { activeLanguage, availableLanguages, switchLanguage, getLanguageInfo } = useLanguageLearning();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const { t } = useTranslation(['pages', 'common']);

  const getDisplayName = (code) => {
    const info = getLanguageInfo(code);
    const translationKey = getLearningLanguageTranslationKey(code);
    if (translationKey) {
      const translatedLabel = t(`common:languageNames.${translationKey}`);
      if (translatedLabel !== `common:languageNames.${translationKey}`) {
        return translatedLabel;
      }
    }
    return info?.name || String(code || '').toUpperCase();
  };

  const getFlagPath = (code) => {
    return getLanguageInfo(code)?.flagPath || null;
  };

  const sortedLanguages = [...availableLanguages].sort((left, right) => {
    return getDisplayName(left.code).localeCompare(getDisplayName(right.code), 'en', {
      sensitivity: 'base',
    });
  });

  const getFlagColors = (code) => {
    const menuTheme = getLanguageInfo(code)?.menuTheme;
    if (menuTheme) {
      return {
        bg: menuTheme.bg,
        bgHover: menuTheme.bgHover,
        border: menuTheme.border,
        text: menuTheme.text,
        shadow: 'rgba(28, 25, 23, 0.4)',
      };
    }
    return {
      bg: '#1c1917',
      bgHover: '#0c0a09',
      border: '#374151',
      text: '#FFFFFF',
      shadow: 'rgba(28, 25, 23, 0.4)',
    };
  };

  const handleSelect = async (code) => {
    const nextPath = currentUser
      ? `/${code}/podcast-lessons`
      : `/${code}/onboarding/welcome`;

    if (code === activeLanguage) {
      // Still navigate even if same language
      navigate(nextPath);
      return;
    }
    try {
      await switchLanguage(code);
    } catch {
      void 0;
    }
    navigate(nextPath, { replace: true });
  };

  const containerVariants = {
    initial: { opacity: 0, y: 15 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <motion.div
      className={`flex flex-col items-center gap-4 ${className}`}
      initial={prefersReducedMotion ? false : containerVariants.initial}
      animate={prefersReducedMotion ? false : containerVariants.animate}
    >
      {showLabel && (
        <span className={`text-sm font-semibold text-stone-600 whitespace-nowrap ${labelClassName}`}>
          {t('pages:hero.startLearning')}
        </span>
      )}

      <div className={`flex flex-wrap items-center justify-center gap-2.5 ${listClassName}`}>
        {sortedLanguages.map((lang, index) => {
          const colors = getFlagColors(lang.code);

          return (
            <motion.button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`flex min-h-[44px] items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D63AB4] focus-visible:ring-offset-2 ${pillClassName}`}
              style={{
                fontFamily: 'var(--font-body)',
                backgroundColor: colors.bg,
                color: colors.text,
                border: `2px solid ${colors.border}`,
              }}
              initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.9 }}
              animate={prefersReducedMotion ? false : {
                opacity: 1,
                scale: 1,
                transition: {
                  duration: 0.2,
                  delay: index * 0.05
                }
              }}
              whileHover={!prefersReducedMotion ? {
                scale: 1.05,
                y: -2,
                backgroundColor: colors.bgHover
              } : {}}
              whileTap={!prefersReducedMotion ? { scale: 0.98 } : {}}
            >
              {getFlagPath(lang.code) && (
                <img
                  src={getFlagPath(lang.code)}
                  alt={`${getDisplayName(lang.code)} flag`}
                  className="h-4 w-5 rounded-sm object-cover"
                  loading="eager"
                />
              )}
              <span>{getDisplayName(lang.code)}</span>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
};

export default HeroLanguagePicker;
